import { Injectable } from '@angular/core';
import { selectCurrentNextLocation } from '@app/next/locations/data-access';
import { CustomerFeature } from '@fmnts/api';
import { isNotNullish } from '@fmnts/core';
import {
  AbstractGraphNodeBuilder,
  DeclarativeFieldComponent,
  FieldNodeInputDefinitionList,
  GraphBuilderContext,
} from '@fmnts/declarative-forms';
import {
  FormGraphNodeBuilder,
  RxJsOperatorNodeName,
} from '@fmnts/declarative-forms/common';
import { CustomerFeatureService } from '@formunauts/shared/customer/data-access';
import { Store } from '@ngrx/store';
import { distinctUntilChanged, filter, map, merge, withLatestFrom } from 'rxjs';

export enum LocationNodeName {
  SelectedLocation = 'location.selected$',
}

const fakeLocationDefinitions: FieldNodeInputDefinitionList = {
  value: {
    name: LocationNodeName.SelectedLocation,
    pipe: [
      {
        operator: RxJsOperatorNodeName.Pluck,
        args: ['id'],
      },
    ],
  },
  isReadonly: {
    name: LocationNodeName.SelectedLocation,
    pipe: [
      {
        operator: RxJsOperatorNodeName.MapTo,
        args: [true],
      },
    ],
  },
};

/**
 * Builder for location related graph nodes
 */
@Injectable({
  providedIn: 'root',
})
export class LocationGraphNodeBuilder extends AbstractGraphNodeBuilder<
  LocationNodeName,
  string
> {
  /** Current location. */
  private readonly _currentLocation$ = this.store.select(
    selectCurrentNextLocation,
  );

  constructor(
    private formSource: FormGraphNodeBuilder,
    private featureService: CustomerFeatureService,
    private store: Store,
  ) {
    super();

    // Provide sources
    this.addNode(LocationNodeName.SelectedLocation, (def, ctx) =>
      this.selectedLocation$(ctx),
    );
  }

  /**
   * @returns
   * An observable that emits with the currently selected location
   * if a location is available.
   */
  private selectedLocation$(ctx: GraphBuilderContext) {
    // This is only available, if the location tracking feature is activated
    if (!this.featureService.on(CustomerFeature.LocationTracking)) {
      return undefined;
    }

    return merge(
      this._currentLocation$,
      // Prevent resetting the form field
      this.formSource.fieldSelfValue$(ctx).pipe(
        distinctUntilChanged(),
        withLatestFrom(this._currentLocation$),
        map(([_, selectedLocation]) => selectedLocation),
      ),
    ).pipe(filter(isNotNullish));
  }

  createFakeDefinitions(ctx: GraphBuilderContext) {
    const genericComponent = ctx.injector.get(DeclarativeFieldComponent, null);

    switch (genericComponent?.field?.key) {
      case 'location':
        return fakeLocationDefinitions;
    }
  }
}
